import React, { useEffect, useState } from "react";
import createTheme from '@mui/material/styles/createTheme';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import TextField from "@mui/material/TextField";
import { NumericFormat, PatternFormat, usePatternFormat, useNumericFormat } from 'react-number-format';
import { IsNullOrEmpty } from "../../../core/utility";
import { CommonInputProps } from "../Models";

const theme = createTheme({
    components: {
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '15px',
                    transform: 'translate(14px, 11px) scale(1)',
                    '&.Mui-focused': {
                        transform: 'translate(14px, -9px) scale(0.85)',
                    },
                    '&.MuiFormLabel-filled': {
                        transform: 'translate(14px, -9px) scale(0.85)',
                    }
                },
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                input: {
                    height: '23px',
                    paddingTop: '10px',
                    lineHeight: '1.5',
                    fontSize: '15px',
                }
            },
        },
    },
});

interface PatternFormatOptions {
    format: string;
    mask?: string | string[];
    allowEmptyFormatting?: boolean;
    patternChar?: string;
}

interface NumericFormatOptions {
    thousandSeparator?: boolean | string;
    decimalSeparator?: string;
    allowedDecimalSeparators?: Array<string>;
    thousandsGroupStyle?: 'thousand' | 'lakh' | 'wan' | 'none';
    decimalScale?: number;
    fixedDecimalScale?: boolean;
    allowNegative?: boolean;
    allowLeadingZeros?: boolean;
    suffix?: string;
    prefix?: string;
}

interface PatternFormatInputProps extends CommonInputProps {
    value: any;
    patternFormat: PatternFormatOptions;
    onChange?: (event: any) => void;
}

interface NumericFormatInputProps extends CommonInputProps {
    value: any;
    numericFormat: NumericFormatOptions | true;
    onChange?: (event: any) => void;
}

interface InputProps extends CommonInputProps {
    value: any;
    multiline?: boolean;
    rows?: number;
    placeholder?: string;
    toUpperCase?: boolean;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    patternFormat?: PatternFormatOptions;
    numericFormat?: NumericFormatOptions | boolean;
    onChange?: (event: any) => void;
    onBlur?: (event: any) => void;
    onKeyPress?: (event: any) => void;
    onKeyDown?: (event: any) => void;
    onKeyUp?: (event: any) => void;
}

const PatternFormatCustom = (props: PatternFormatInputProps) => {

    const { patternFormat } = props;

    const [value, setValue] = useState<string | null | undefined>('');

    const { format } = usePatternFormat({
        ...patternFormat,
    });

    useEffect(() => {
        let inputValue = props.value;
        inputValue = !IsNullOrEmpty(inputValue) ? format(inputValue) : '';
        setValue(inputValue);
    }, [props.value])

    return (
        <ThemeProvider theme={theme}>
            <PatternFormat
                id={props.id}
                name={props.name}
                label={`${props.caption} ${props.required ? "*" : ""}`}
                type="text"
                size="small"
                fullWidth
                variant="outlined"
                disabled={props.disabled}
                error={!IsNullOrEmpty(props.errorText)}
                helperText={props.errorText ?? null}
                format={patternFormat.format}
                mask={patternFormat.mask}
                allowEmptyFormatting={patternFormat.allowEmptyFormatting}
                patternChar={patternFormat.patternChar}
                customInput={TextField}
                autoComplete="off"
                value={value === undefined || props.value === '' ? '' : value}
                onValueChange={(values) => {
                    setValue(values.formattedValue);
                    if (props.onChange) {
                        props.onChange({
                            target: {
                                name: props.name,
                                value: values.value,
                                formattedValue: values.formattedValue
                            },
                        });
                    }
                }}
            />
        </ThemeProvider>
    )
}

const NumericFormatCustom = (props: NumericFormatInputProps) => {

    const { numericFormat } = props;

    const [value, setValue] = useState<string | number | null | undefined>('');

    const numericFormatOptions = typeof numericFormat === "boolean" && numericFormat === true ? {} : numericFormat;

    const { format } = useNumericFormat({
        ...numericFormatOptions
    });

    useEffect(() => {
        let inputValue = String(props.value);
        inputValue = !IsNullOrEmpty(inputValue) ? format(inputValue) : '';
        setValue(inputValue);
    }, [props.value])

    return (
        <ThemeProvider theme={theme}>
            <NumericFormat
                id={props.id}
                name={props.name}
                label={`${props.caption} ${props.required ? "*" : ""}`}
                type="text"
                size="small"
                fullWidth
                variant="outlined"
                disabled={props.disabled}
                error={!IsNullOrEmpty(props.errorText)}
                helperText={props.errorText ?? null}
                customInput={TextField}
                autoComplete="off"
                thousandSeparator={numericFormatOptions.thousandSeparator}
                decimalSeparator={numericFormatOptions.decimalSeparator}
                allowedDecimalSeparators={numericFormatOptions.allowedDecimalSeparators}
                thousandsGroupStyle={numericFormatOptions.thousandsGroupStyle}
                decimalScale={numericFormatOptions.decimalScale}
                fixedDecimalScale={numericFormatOptions.fixedDecimalScale}
                allowNegative={numericFormatOptions.allowNegative}
                allowLeadingZeros={numericFormatOptions.allowLeadingZeros}
                suffix={numericFormatOptions.suffix}
                prefix={numericFormatOptions.prefix}
                value={value === undefined || props.value === '' ? '' : value}
                onValueChange={(values) => {
                    setValue(values.floatValue);
                    if (props.onChange) {
                        props.onChange({
                            target: {
                                name: props.name,
                                value: values.floatValue,
                                formattedValue: values.formattedValue
                            },
                        });
                    }
                }}
            />
        </ThemeProvider>
    )
}

const TextFieldCustom = (props: InputProps) => {

    const [value, setValue] = useState<any>('');

    useEffect(() => {
        setValue(props.value === undefined || props.value === '' ? '' : props.value)
    }, [props.value])

    return (
        <ThemeProvider theme={theme}>
            <TextField
                id={props.id}
                name={props.name}
                label={`${props.caption}`}
                variant="outlined"
                type="text"
                size="small"
                className={props.className}
                fullWidth
                required={props.required}
                multiline={props.multiline}
                rows={props.rows}
                disabled={props.disabled}
                autoComplete="off"
                InputProps={{
                    readOnly: props.readOnly,
                    disabled: props.disabled,
                    startAdornment: props.startAdornment,
                    endAdornment: props.endAdornment,
                }}
                placeholder={props.placeholder}
                error={!IsNullOrEmpty(props.errorText)}
                helperText={props.errorText ?? null}
                value={value}
                onChange={(e) => {
                    if (props.toUpperCase) {
                        e.target.value = ("" + e.target.value).toLocaleUpperCase('tr-TR');
                    }
                    setValue(e.target.value);
                    if (props.onChange) {
                        props.onChange(e);
                    }
                }}
                onBlur={props.onBlur}
                onKeyPress={props.onKeyPress}
                onKeyDown={props.onKeyDown}
                onKeyUp={props.onKeyUp}
            />
        </ThemeProvider>
    )
}

const Input = (props: InputProps) => {

    const { patternFormat, numericFormat } = props;

    if (patternFormat) {
        return <PatternFormatCustom
            {...props}
            patternFormat={patternFormat}
        />
    }

    if (numericFormat) {
        return <NumericFormatCustom
            {...props}
            numericFormat={numericFormat}
        />
    }

    return <TextFieldCustom {...props} />
}

export default Input;